import { assertNever } from 'types/assertNever'
import cycleOptions from 'utils/cycleOptions'

import {
  CycleDirection,
  FormatterOption,
  FormatterOptions,
  QFManagerContextType,
  USDOnlyFormatterOptions,
} from './types'

export const defaultCurrencyOptions = ['Native', 'BTC', 'USD']

export type QFManagerContextState = Pick<
  QFManagerContextType,
  'currencyOptions' | 'selectedCurrency' | 'selectedFormatter'
>

export const defaultQFManagerContextState: QFManagerContextState = {
  currencyOptions: [...defaultCurrencyOptions],
  selectedCurrency: 'Native',
  selectedFormatter: 'Compact',
}

export type Actions =
  | {
      type: 'set_currency_options'
      currencyOptions: string[]
      fallbackCurrency?: string
    }
  | { type: 'set_selected_currency'; selectedCurrency: string }
  | { type: 'cycle_selected_currency'; direction: CycleDirection }
  | { type: 'set_selected_formatter'; selectedFormatter: FormatterOption }
  | { type: 'cycle_selected_formatter'; direction: CycleDirection }

const setCurrencyOptions = (
  prevState: QFManagerContextState,
  currencyOptions: string[],
  fallbackCurrency: string | undefined,
): QFManagerContextState => ({
  ...prevState,
  currencyOptions,
  selectedCurrency: currencyOptions.includes(prevState.selectedCurrency)
    ? prevState.selectedCurrency
    : (fallbackCurrency ?? currencyOptions[0]),
})

const setSelectedCurrency = (
  prevState: QFManagerContextState,
  selectedCurrency: string,
): QFManagerContextState => {
  // If we've changed currency away from USD and we're using a USD-only
  // formatter then we should revert to using the default formatter instead
  const selectedFormatter =
    selectedCurrency !== 'USD' &&
    USDOnlyFormatterOptions.includes(prevState.selectedFormatter)
      ? defaultQFManagerContextState.selectedFormatter
      : prevState.selectedFormatter

  return {
    ...prevState,
    // If for some reason the currency we're trying to select isn't in the list
    // of options, add it. This is sort of a programmer error, but this means
    // the currency selection UX will still be OK.
    currencyOptions: prevState.currencyOptions.includes(selectedCurrency)
      ? prevState.currencyOptions
      : [...prevState.currencyOptions, selectedCurrency],
    selectedCurrency,
    selectedFormatter,
  }
}

const setSelectedFormatter = (
  prevState: QFManagerContextState,
  selectedFormatter: FormatterOption,
): QFManagerContextState => {
  // Make sure we change the currency to USD if the formatter only supports USD
  const selectedCurrency = USDOnlyFormatterOptions.includes(selectedFormatter)
    ? 'USD'
    : prevState.selectedCurrency

  return {
    ...prevState,
    selectedCurrency,
    selectedFormatter,
  }
}

export const stateReducer: React.Reducer<QFManagerContextState, Actions> = (
  prevState,
  action,
) => {
  switch (action.type) {
    case 'set_currency_options':
      return setCurrencyOptions(
        prevState,
        action.currencyOptions,
        action.fallbackCurrency,
      )
    case 'set_selected_currency':
      return setSelectedCurrency(prevState, action.selectedCurrency)
    case 'cycle_selected_currency':
      return setSelectedCurrency(
        prevState,
        cycleOptions(prevState.currencyOptions)(
          prevState.selectedCurrency,
          action.direction === 'forward' ? 1 : -1,
        ),
      )
    case 'set_selected_formatter':
      return setSelectedFormatter(prevState, action.selectedFormatter)
    case 'cycle_selected_formatter':
      return setSelectedFormatter(
        prevState,
        cycleOptions([...FormatterOptions])(
          prevState.selectedFormatter,
          action.direction === 'forward' ? 1 : -1,
        ),
      )
    default:
      return assertNever(action)
  }
}
